/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { decode, encode } from 'cbor-web';
import { RSocketBaseService } from './RSocketBaseService';
import { RSocketStreamClientInterface } from './RSocketObject';

// T is the local cache type, RESP is the response type
// Local cache is used to store the stream data and gets passed to listeners
// Response type is the type of the data received from the stream
export abstract class RSocketStreamBaseService<T, RESP> extends RSocketBaseService<T, RESP> {
  protected async createStream(): Promise<RSocketStreamClientInterface | undefined> {
    // Get the socket connection based on the sanctioning body and connection type (this was created in the createSocket method)
    const socket = RSocketBaseService.socket.get(this.sanctioningBody)?.get(this.connnectionType);
    if (socket) {
      console.log('Stream Created:', { route: this.route, streamRequestData: this.request });
      const requester = socket.requestStream(
        {
          data: encode(this.request),
          metadata: this.buildMetadata(),
        },
        400, // this indicates to the backend how many payloads the requester can handle
        {
          onError: (e: any) => {
            console.error(e);
            requester.cancel();
            this.reconnectToStream();
          },
          // This method is called whenever a message is received from the stream
          onNext: (payload, isComplete) => {
            const data: RESP = decode(payload.data as Buffer);
            // Update the local cache in the onDataCallback method (this is overridden in the child class)
            this.onDataCallback(data);
            this.numOfRequests += 1;
            // If the backend has sent 400 requests, send another request for more data
            if (this.numOfRequests % 400 === 0) {
              this.numOfRequests = 0;
              requester.request(400);
            }

            if (isComplete) {
              requester.cancel();
            }
          },
          // If the stream sends an error or complete message, reconnect to the stream
          onComplete: () => {
            console.log(`${this.route} complete`);
            this.reconnectToStream(true);
          },
          onExtension: () => {},
        }
      );

      return requester;
    } else {
      this.reconnectToStream();
    }
  }
}
